(() => {
  if (customElements.get('ld-lead-popup')) return;
  class LuzDivinaLeadPopup extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.countryInitialized = new WeakSet();
      const options = { signal: this.controller.signal };
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.preview = new URLSearchParams(location.search).get('ld_popup') === 'preview' || this.dataset.editor === 'true';
      this.key = 'ld-popup-forms-v1';
      this.launcher = this.querySelector('[data-launcher]');
      this.launcher.addEventListener('click', () => this.open(), options);
      this.querySelectorAll('[data-close]').forEach(button => button.addEventListener('click', () => this.close(), options));
      this.querySelectorAll('[data-choice]').forEach(button => button.addEventListener('click', () => {
        this.choice = button.dataset.choice;
        this.querySelector('[data-answer]').textContent = button.textContent;
        this.querySelectorAll('[data-form-choice]').forEach(form => { form.hidden = form.dataset.formChoice !== this.choice; });
        this.step('capture');
      }, options));
      this.querySelector('[data-back]').addEventListener('click', () => this.step('question'), options);
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
      }, options);
      document.addEventListener('ld-popup:open', () => this.open(), options);
      document.addEventListener('shopify:section:select', event => {
        if (event.detail.sectionId === this.dataset.sectionId) this.open();
      }, options);
      document.addEventListener('shopify:block:select', event => {
        const wrapper = Array.from(this.querySelectorAll('[data-shopify-editor-block]')).find(element => {
          try { return JSON.parse(element.dataset.shopifyEditorBlock).id === event.detail.blockId; }
          catch { return false; }
        });
        if (!wrapper) return;
        this.open();
        this.querySelector('[data-choice="' + wrapper.dataset.formChoice + '"]')?.click();
      }, options);
      if (this.dataset.editor === 'true') this.timer = setTimeout(() => this.open(), 200);
      else if (this.eligible()) {
        this.scheduleEntrance();
      }
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.stopCountryDefault?.();
      if (this.dialog?.open) this.close(false);
      this.controller?.abort();
      this.controller = null;
    }
    isReady() {
      return this.dataset.ready === 'true' && this.querySelectorAll('[data-form-choice]').length === 3;
    }
    eligible() {
      return (this.preview || this.isReady()) && this.dataset.enabled === 'true' && this.dataset.customer !== 'true';
    }
    sessionValue(key) {
      try { return sessionStorage.getItem(key); } catch { return null; }
    }
    scheduleEntrance() {
      if (!this.eligible()) return;
      if (this.suppressed() || this.sessionValue('ld-popup-shown')) {
        this.launcher.hidden = false;
        return;
      }
      this.entranceDeadline = Date.now() + 30000;
      this.timer = setTimeout(() => this.maybeOpen(), 3000);
    }
    suppressed() {
      try { return JSON.parse(localStorage.getItem(this.key))?.until > Date.now(); }
      catch { return false; }
    }
    maybeOpen() {
      if (!this.eligible() || this.suppressed() || this.sessionValue('ld-popup-shown') || this.closedThisPage) return;
      if (document.hidden || document.querySelector('dialog[open], .menu-drawer-container[open], cart-drawer.active, .polaris-recommendation-modal.is-open')) {
        this.launcher.hidden = false;
        if (Date.now() < this.entranceDeadline) this.timer = setTimeout(() => this.maybeOpen(), 1000);
        return;
      }
      if (!this.suppressed()) this.open();
    }
    open() {
      if (!this.preview && !this.isReady()) return;
      if (this.dialog.open) return;
      clearTimeout(this.timer);
      this.launcher.hidden = true;
      try { sessionStorage.setItem('ld-popup-shown', 'true'); } catch {}
      this.lastFocus = document.activeElement;
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.dialog.showModal();
      this.step(this.currentStep || 'question');
    }
    close(remember = true) {
      clearTimeout(this.timer);
      this.stopCountryDefault?.();
      if (!this.dialog.open) return;
      this.dialog.close();
      document.body.style.overflow = this.previousOverflow || '';
      this.closedThisPage = true;
      if (remember) this.launcher.hidden = false;
      if (remember) {
        try { localStorage.setItem(this.key, JSON.stringify({ until: Date.now() + Number(this.dataset.repeatDays) * 86400000 })); } catch {}
      }
      if (this.lastFocus?.isConnected) this.lastFocus.focus({ preventScroll: true });
    }
    step(name) {
      this.stopCountryDefault?.();
      this.currentStep = name;
      this.querySelectorAll('[data-step]').forEach(element => { element.hidden = element.dataset.step !== name; });
      const title = this.querySelector('[data-step="' + name + '"] h2');
      this.dialog.setAttribute('aria-labelledby', title.id || (title.id = 'ld-' + name + '-' + this.dataset.sectionId));
      this.dialog.scrollTop = 0;
      title.focus({ preventScroll: true });
      if (name === 'capture') this.defaultPhoneCountry(title);
    }
    defaultPhoneCountry(title) {
      const wrapper = this.querySelector('[data-form-choice="' + this.choice + '"]');
      if (!wrapper || this.countryInitialized.has(wrapper)) return;
      // Forms has no merchant-facing default-country setting. Use its own
      // country controls, so React keeps the flag, prefix and submission in sync.
      // Keep this adapter scoped, bounded, and never override visitor input.
      const started = Date.now();
      let timer, root, opened = false;
      const stop = () => {
        clearTimeout(timer);
        root?.removeEventListener('pointerdown', touched, true);
        root?.removeEventListener('keydown', touched, true);
        root?.removeEventListener('input', touched, true);
        if (this.stopCountryDefault === stop) this.stopCountryDefault = null;
      };
      const touched = event => {
        if (!event.isTrusted) return;
        this.countryInitialized.add(wrapper);
        stop();
      };
      const retry = () => { timer = setTimeout(attempt, 100); };
      const attempt = () => {
        if (!this.isConnected || !this.dialog.open || wrapper.hidden || Date.now() - started > 8000) { stop(); return; }
        const nextRoot = wrapper.querySelector('shopify-forms-embed')?.shadowRoot;
        if (!nextRoot) { retry(); return; }
        if (!root) {
          root = nextRoot;
          ['pointerdown', 'keydown', 'input'].forEach(type => root.addEventListener(type, touched, true));
        }
        const country = root.querySelector('.phone-country-selector button');
        const phone = root.querySelector('input[type="tel"]');
        if (!country || !phone) { retry(); return; }
        const prefix = country.getAttribute('aria-label')?.match(/\+(\d+)\s*$/)?.[1];
        const digits = phone.value.replace(/\D/g, '');
        if (!prefix || (digits && digits !== prefix)) { this.countryInitialized.add(wrapper); stop(); return; }
        if (country.querySelector('img[src$="/co.svg"]')) {
          this.countryInitialized.add(wrapper);
          stop();
          if (opened) title.focus({ preventScroll: true });
          return;
        }
        if (!opened) {
          if (country.getAttribute('aria-expanded') === 'true') { stop(); return; }
          opened = true;
          country.click();
        } else {
          const colombia = root.querySelector('#select-country-CO');
          if (colombia) colombia.click();
        }
        retry();
      };
      this.stopCountryDefault = stop;
      attempt();
    }
  }
  customElements.define('ld-lead-popup', LuzDivinaLeadPopup);
})();
